import React, { useRef, useEffect } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "./TextEditor.scss";

/** Apply HTML via Quill's delta pipeline — never set quill.root.innerHTML (breaks Quill 2 cursor/IME). */
function setHtmlFromProp(quill, html) {
  const safe = html && String(html).trim() !== "" ? html : "<p><br></p>";
  const delta = quill.clipboard.convert({ html: safe });
  quill.setContents(delta, Quill.sources.SILENT);
}

/** Quill inserts toolbars as siblings of the container — clear them on teardown. */
function destroyQuillMount(container, quill) {
  const wrapper = container?.parentElement;
  if (wrapper) {
    wrapper.querySelectorAll(".ql-toolbar").forEach((toolbar) => toolbar.remove());
  }
  if (container) {
    container.className = "text-editor-container";
    container.innerHTML = "";
    if (container.__quill) {
      delete container.__quill;
    }
  }
  if (quill) {
    quill.off("text-change");
  }
}

const TextEditor = ({
  value = "",
  onChange = () => {},
  placeholder = "",
  disabled = false,
  height = "18rem",
  minHeight = "14rem",
  className = "",
  error = false,
  /** When false, hides the image toolbar button; link remains. Default true. */
  showImageButton = true,
  /** When false, hides the link toolbar button. Default true. */
  showLinkButton = true,
  /** When false, hides the code-block toolbar button. Default true. */
  showCodeBlock = true,
  /** "full" (default) or "compact" — compact uses a single-row toolbar for narrow panels. */
  toolbarVariant = "full",
  /** When false, hides the clear-formatting (Tx) toolbar button. Default true. */
  showCleanButton = true,
  /**
   * When true: no image toolbar, strip pasted/dropped images (use file attachments instead).
   * Implies hiding the image button even if showImageButton is true.
   */
  blockImages = false,
  /**
   * Strip script/iframe/embed and block data: URLs on paste (use with validateContentBeforeApply).
   */
  sanitizeHtml = false,
  /** Return { allowed: false, message? } to block paste or undo typed content. */
  validateContentBeforeApply,
  onContentBlocked,
}) => {
  const quillContainerRef = useRef(null);
  const quillInstanceRef = useRef(null);
  /** Last HTML we emitted via text-change — skip re-applying when parent echoes it (avoids Cursor/composing crashes). */
  const lastEmittedHtmlRef = useRef(value);
  const validateRef = useRef(validateContentBeforeApply);
  const onContentBlockedRef = useRef(onContentBlocked);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    validateRef.current = validateContentBeforeApply;
  }, [validateContentBeforeApply]);

  useEffect(() => {
    onContentBlockedRef.current = onContentBlocked;
  }, [onContentBlocked]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Initialize Quill editor (rebuild only when toolbar options change)
  useEffect(() => {
    if (!quillContainerRef.current) return;

    const container = quillContainerRef.current;
    const Delta = Quill.import("delta");
    const allowToolbarImage = showImageButton && !blockImages;
    const enablePasteValidation = Boolean(validateRef.current) || sanitizeHtml;
    const stripUnsafePaste = blockImages || sanitizeHtml;

    const formatOptions = ["blockquote"];
    if (showCodeBlock) {
      formatOptions.push("code-block");
    }

    const linkImageOptions = [];
    if (allowToolbarImage) {
      linkImageOptions.push("link", "image");
    } else if (showLinkButton) {
      linkImageOptions.push("link");
    }

    const cleanOption = showCleanButton ? [["clean"]] : [];

    const toolbarOptions =
      toolbarVariant === "compact"
        ? [
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            [{ color: [] }],
            ...cleanOption,
          ]
        : [
            [{ font: [] }, { size: [] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ script: "sub" }, { script: "super" }],
            [{ header: 1 }, { header: 2 }],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
            formatOptions,
            ...(linkImageOptions.length ? [linkImageOptions] : []),
            ...cleanOption,
          ];

    const quill = new Quill(container, {
      theme: "snow",
      modules: { toolbar: toolbarOptions },
      placeholder: placeholder,
      readOnly: disabled,
    });

    container.__quill = quill;
    quillInstanceRef.current = quill;

    /** Strip unsafe nodes from HTML paste conversions. */
    if (blockImages) {
      quill.clipboard.addMatcher("IMG", () => new Delta());
      quill.clipboard.addMatcher("PICTURE", () => new Delta());
    }

    if (sanitizeHtml) {
      const stripNode = () => new Delta();
      ["SCRIPT", "IFRAME", "OBJECT", "EMBED", "STYLE"].forEach((tag) => {
        quill.clipboard.addMatcher(tag, stripNode);
      });
    }

    /** Prevent paste when clipboard is image-only (e.g. screenshot). */
    const onPasteCapture = (e) => {
      const cd = e.clipboardData;
      const validate = validateRef.current;
      if (validate && cd) {
        const html = cd.getData("text/html") || "";
        const text = cd.getData("text/plain") || "";
        const probe = html.trim() ? html : text;
        const result = validate(probe);
        if (result && result.allowed === false) {
          e.preventDefault();
          e.stopPropagation();
          onContentBlockedRef.current?.(result.message);
          return;
        }
      }

      if (!stripUnsafePaste) return;
      if (!cd || !cd.items || !cd.items.length) return;
      const items = Array.from(cd.items);
      const hasImageFile = items.some(
        (it) => it.kind === "file" && it.type && it.type.startsWith("image/"),
      );
      const hasHtml = items.some((it) => it.type === "text/html");
      const hasText = items.some((it) => it.type === "text/plain");
      if (hasImageFile && !hasHtml && !hasText) {
        e.preventDefault();
        e.stopPropagation();
        onContentBlockedRef.current?.("Images cannot be pasted into email templates.");
        return;
      }
      const pastedHtml = cd.getData("text/html") || "";
      const pastedText = cd.getData("text/plain") || "";
      const probe = pastedHtml.trim() ? pastedHtml : pastedText;
      if (/<img\b/i.test(pastedHtml) || /data:image\//i.test(pastedHtml)) {
        e.preventDefault();
        e.stopPropagation();
        onContentBlockedRef.current?.("Images cannot be pasted into email templates.");
        return;
      }
      if (sanitizeHtml && /data\s*:/i.test(probe)) {
        e.preventDefault();
        e.stopPropagation();
        onContentBlockedRef.current?.("Embedded data (e.g. base64) is not allowed.");
        return;
      }
      if (sanitizeHtml && /<script[\s>]/i.test(probe)) {
        e.preventDefault();
        e.stopPropagation();
        onContentBlockedRef.current?.("Scripts are not allowed.");
        return;
      }
    };

    /** Block dropping image files into the editor. */
    const onDropCapture = (e) => {
      if (!blockImages) return;
      const dt = e.dataTransfer;
      if (!dt || !dt.files || !dt.files.length) return;
      for (let i = 0; i < dt.files.length; i += 1) {
        if (dt.files[i].type && dt.files[i].type.startsWith("image/")) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    };

    if (blockImages || enablePasteValidation) {
      quill.root.addEventListener("paste", onPasteCapture, true);
      if (blockImages) {
        quill.root.addEventListener("drop", onDropCapture, true);
      }
    }

    if (value) {
      setHtmlFromProp(quill, value);
    }
    lastEmittedHtmlRef.current = quill.root.innerHTML;

    const onTextChange = (delta, oldContents, source) => {
      const validate = validateRef.current;
      if (validate && source === Quill.sources.USER) {
        const html = quill.root.innerHTML;
        const result = validate(html);
        if (result && result.allowed === false) {
          quill.setContents(oldContents, Quill.sources.SILENT);
          lastEmittedHtmlRef.current = quill.root.innerHTML;
          onContentBlockedRef.current?.(result.message);
          return;
        }
      }

      const html = quill.root.innerHTML;
      lastEmittedHtmlRef.current = html;
      onChangeRef.current(html);
    };

    quill.on("text-change", onTextChange);

    return () => {
      if (quill?.root) {
        quill.root.removeEventListener("paste", onPasteCapture, true);
        if (blockImages) {
          quill.root.removeEventListener("drop", onDropCapture, true);
        }
      }
      destroyQuillMount(container, quill);
      quillInstanceRef.current = null;
    };
  }, [showImageButton, showLinkButton, showCodeBlock, showCleanButton, blockImages, sanitizeHtml, toolbarVariant]);

  // Update content when value prop changes (controlled component)
  useEffect(() => {
    const quill = quillInstanceRef.current;
    if (!quill || value === undefined) return;

    // Parent is echoing our own update — do not mutate the editor (prevents Quill internal errors while typing).
    if (value === lastEmittedHtmlRef.current) return;

    const currentContent = quill.root.innerHTML;
    if (currentContent === value) return;

    setHtmlFromProp(quill, value || "");
    lastEmittedHtmlRef.current = quill.root.innerHTML;
  }, [value]);

  // Update disabled state
  useEffect(() => {
    if (quillInstanceRef.current) {
      quillInstanceRef.current.enable(!disabled);
    }
  }, [disabled]);

  return (
    <div className={`text-editor-wrapper ${className} ${error ? "error" : ""}`}>
      <div
        ref={quillContainerRef}
        className="text-editor-container"
        style={{ "--editor-height": height, "--editor-min-height": minHeight }}
      />
    </div>
  );
};

export default TextEditor;
